import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiClient, formatApiErrorDetail } from "../../lib/api";
import { Header } from "../../components/vc/Header";
import { PrimaryButton, SecondaryButton } from "../../components/vc/Buttons";
import { Input } from "../../components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import { BusinessMultiSelect } from "../../components/vc/BusinessMultiSelect";
import { ArrowLeft, Plus, Trash2, Sparkles, Send, CornerDownLeft, X } from "lucide-react";
import { toast } from "sonner";

const UNITS = ["pcs", "kg", "ltr", "box", "bag", "meter", "gram", "dozen"];

export default function VCAddEnquiryItems() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [enq, setEnq] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [rows, setRows] = useState([{ product: "", quantity: 1, unit: "pcs", supplier_ids: [] }]);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      apiClient.get(`/bulk-price-requests/${id}`),
      apiClient.get(`/suppliers`),
    ])
      .then(([e, s]) => {
        setEnq(e.data);
        setSuppliers(s.data || []);
      })
      .catch(() => {
        toast.error("Enquiry not found");
        navigate("/enquiries");
      })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const fullyClosed = useMemo(() => {
    if (!enq) return false;
    const items = enq.items || [];
    return items.length > 0 && items.every((it) => it.order_id);
  }, [enq]);

  useEffect(() => {
    if (!loading && fullyClosed) {
      toast.error("This enquiry is closed — every item has been ordered.");
      navigate(`/enquiries/${id}`, { replace: true });
    }
  }, [loading, fullyClosed, id, navigate]);

  const update = (i, k, v) =>
    setRows((r) => r.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));
  const remove = (i) => setRows((r) => r.filter((_, idx) => idx !== i));
  const addRow = () =>
    setRows((r) => [...r, { product: "", quantity: 1, unit: "pcs", supplier_ids: [] }]);
  const sameAsAbove = (i) => {
    if (i === 0) return;
    setRows((r) => r.map((row, idx) => (idx === i ? { ...row, supplier_ids: [...(r[i - 1].supplier_ids || [])] } : row)));
    toast.success("Copied businesses from above");
  };

  const submit = async () => {
    const valid = rows.filter((r) =>
      (r.product || "").trim().length > 0 && Number(r.quantity) > 0 && (r.supplier_ids || []).length > 0
    );
    if (valid.length === 0) {
      toast.error("Add at least one row with product, qty and business");
      return;
    }
    setSubmitting(true);
    try {
      const items = valid.map((r) => ({
        product: r.product.trim(),
        quantity: Number(r.quantity),
        unit: r.unit || "pcs",
        supplier_ids: r.supplier_ids,
      }));
      await apiClient.post(`/bulk-price-requests/${id}/items`, { items });
      toast.success(`Added ${items.length} item${items.length === 1 ? "" : "s"} to the enquiry`);
      navigate(`/enquiries/${id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to add items");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !enq) {
    return (
      <>
        <Header />
        <div className="px-4 py-8 text-center text-slate-500">Loading enquiry...</div>
      </>
    );
  }

  const displayTitle = (enq.title && enq.title.trim()) || `#${(enq.id || "").slice(0, 6).toUpperCase()}`;

  return (
    <>
      <Header />
      <div className="px-4 pt-4 pb-32 space-y-4">
        <button
          onClick={() => navigate(`/enquiries/${id}`)}
          data-testid="add-items-back"
          className="flex items-center gap-1 text-sm font-semibold text-slate-600"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div>
          <div className="inline-flex items-center gap-1.5 text-emerald-600 text-[10px] font-bold uppercase tracking-widest mb-1">
            <Sparkles className="w-3 h-3" strokeWidth={2.6} /> Add Items
          </div>
          <h2 className="text-2xl font-bold text-slate-900 leading-tight">Add items to enquiry</h2>
          <p className="text-sm text-slate-500 mt-0.5 truncate" data-testid="add-items-title-context">
            Under <span className="font-semibold text-slate-800">{displayTitle}</span>
          </p>
        </div>

        <div className="space-y-2" data-testid="add-items-rows">
          {rows.map((row, i) => (
            <div
              key={i}
              data-testid={`add-item-row-${i}`}
              className="bg-white border border-slate-100 rounded-2xl p-2 shadow-sm space-y-1.5"
            >
              <div className="flex items-center gap-1.5">
                <span className="w-4 sm:w-5 shrink-0 text-center text-[11px] font-bold text-slate-400 tabular-nums">{i + 1}.</span>
                <Input
                  data-testid={`add-item-product-${i}`}
                  value={row.product}
                  onChange={(e) => update(i, "product", e.target.value)}
                  placeholder="Product"
                  className="h-10 rounded-lg flex-1 min-w-0 text-sm"
                />
                <Input
                  data-testid={`add-item-qty-${i}`}
                  type="number"
                  min="0"
                  step="0.01"
                  value={row.quantity}
                  onChange={(e) => update(i, "quantity", e.target.value)}
                  placeholder="Qty"
                  className="h-10 rounded-lg w-10 sm:w-12 text-sm text-right tabular-nums px-1.5"
                />
                <Select value={row.unit} onValueChange={(v) => update(i, "unit", v)}>
                  <SelectTrigger data-testid={`add-item-unit-${i}`} className="h-10 rounded-lg w-[54px] sm:w-[62px] text-sm px-1.5 shrink-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="min-w-[115px] flex-[2] shrink sm:min-w-0 sm:flex-1">
                  <BusinessMultiSelect
                    businesses={suppliers}
                    selectedIds={row.supplier_ids || []}
                    onChange={(next) => update(i, "supplier_ids", next)}
                    testidPrefix={`add-item-biz-${i}`}
                    placeholder="Business"
                    compact
                  />
                </div>
                <button
                  onClick={() => remove(i)}
                  data-testid={`add-item-delete-${i}`}
                  className="w-7 sm:w-8 h-10 shrink-0 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 grid place-items-center transition"
                  aria-label="Remove"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>

              {((i === 0 && rows.length > 1) || i > 0 || (row.supplier_ids || []).length > 0) && (
                <div className="flex items-center flex-wrap gap-1.5 pl-6">
                  {i === 0 && rows.length > 1 && (
                    <button
                      onClick={() => {
                        const first = rows[0]?.supplier_ids || [];
                        setRows((rs) => rs.map((r, idx) => (idx === 0 ? r : { ...r, supplier_ids: [...first] })));
                        toast.success("Applied to all items");
                      }}
                      data-testid="add-item-apply-to-all"
                      className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 hover:text-emerald-800 normal-case shrink-0"
                    >
                      Apply to All
                    </button>
                  )}
                  {i > 0 && (
                    <button
                      onClick={() => sameAsAbove(i)}
                      data-testid={`add-item-same-as-above-${i}`}
                      className="text-[10px] font-bold uppercase tracking-widest text-indigo-600 hover:text-indigo-700 flex items-center gap-1 normal-case shrink-0"
                    >
                      <CornerDownLeft className="w-3 h-3" /> Same as above
                    </button>
                  )}
                  {(row.supplier_ids || []).length > 0 && (
                    <div className="flex flex-wrap gap-1" data-testid={`add-item-chips-${i}`}>
                      {(row.supplier_ids || []).map((sid) => {
                        const s = suppliers.find((x) => x.id === sid);
                        if (!s) return null;
                        return (
                          <span
                            key={sid}
                            className="inline-flex items-center gap-1 h-6 pl-2 pr-0.5 rounded-full bg-emerald-600 text-white text-[10px] font-semibold"
                          >
                            <span className="max-w-[100px] truncate">{s.name}</span>
                            <button
                              onClick={() => update(i, "supplier_ids", (row.supplier_ids || []).filter((x) => x !== sid))}
                              className="w-4 h-4 rounded-full bg-white/20 hover:bg-white/30 grid place-items-center"
                              aria-label={`Remove ${s.name}`}
                            >
                              <X className="w-2.5 h-2.5" />
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
          {suppliers.length === 0 && (
            <div className="text-xs text-slate-500 bg-slate-50 rounded-xl p-2">No connected businesses yet.</div>
          )}
          <SecondaryButton onClick={addRow} data-testid="add-item-add-row" className="w-full">
            <Plus className="w-4 h-4 inline mr-1" /> Add Another Item
          </SecondaryButton>
        </div>
      </div>

      {/* Sticky submit */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-100 p-4 z-30">
        <div className="max-w-md mx-auto">
          <PrimaryButton
            onClick={submit}
            disabled={submitting}
            data-testid="add-items-submit"
            className="w-full"
          >
            <Send className="w-4 h-4 inline mr-1.5" />
            {submitting ? "Sending..." : "Add & Send to businesses"}
          </PrimaryButton>
        </div>
      </div>
    </>
  );
}
